import { toast } from 'sonner';

/**
 * Robust copy-to-clipboard that works even when navigator.clipboard is
 * unavailable (older browsers, insecure contexts) or blocked by focus
 * trapping inside modals/dropdowns. Falls back to the legacy execCommand
 * path via a hidden textarea.
 *
 * Always resolves. Fires a sonner toast on both success and failure so the
 * user gets immediate feedback.
 */
export const copyToClipboard = async (value, label = 'Text') => {
  if (value == null) {
    toast.error('Nothing to copy');
    return false;
  }
  const str = String(value);

  // Primary path — modern Clipboard API
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(str);
      toast.success(`${label} copied`);
      return true;
    }
  } catch (_) {
    // fall through to legacy path
  }

  // Legacy fallback — execCommand('copy') via a hidden textarea
  try {
    const ta = document.createElement('textarea');
    ta.value = str;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.opacity = '0';
    ta.style.pointerEvents = 'none';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, str.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) {
      toast.success(`${label} copied`);
      return true;
    }
  } catch (_) { /* fall through */ }

  toast.error('Copy failed — please long-press to select and copy manually');
  return false;
};
